import { ReactNode, useState } from 'react'
import {
  AccountBalance,
  Autorenew,
  Cancel,
  Celebration,
  CheckCircle,
  DeleteOutline,
  EditOutlined,
  ExpandMore,
  HourglassTop,
  InfoOutlined,
  Draw,
  PersonAdd,
} from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material'
import { CollegeOption } from '@/api/financial/application/college'
import {
  FinancialPerson,
  FinancialProposal,
} from '@/api/financial/application/read-application'
import { InternshipPreview } from '@/api/financial/simulation/simulate-internship'
import { MunicipalityOption } from '@/api/financial/person/municipalities'
import { CepSearchField } from '@/components/address-form/cep-search-field'
import { RouterLink } from '@/components/router-link'
import {
  Field,
  MoneyField,
  OptionCards,
  PillGroup,
  ToggleRow,
} from '@/components/credit-journey/fields'
import { DocumentDropzone } from '@/components/document-input/document-dropzone'
import { MaskedInput } from '@/components/masked-input/masked-input'
import { states } from '@/constants/states'
import { isMarried } from '@/pages/credit/journeys/allowance/allowance-flow'
import {
  AllowanceGraduation,
  HIGH_SCHOOL,
  Locked,
  MARITAL,
  PAYMENT_SOURCES,
  PEP_INFO,
  PLANS,
  RESIDENCY,
  SCHOOL,
  US_PERSON_INFO,
  YES_NO,
  brazilDate,
  pct,
  reais,
} from '@/pages/credit/journeys/allowance/allowance-screens'
import { routes } from '@/routes'
import { CepResponse } from '@/types'
import {
  BackerRole,
  ENABLING_LETTER,
  InternshipStep,
  MAX_GUARANTORS,
  editorRole,
} from './internship-flow'
import { BackerForm, InternshipForm, backerAgeOk } from './internship-form'

const RELATIONSHIPS = [
  { value: 'parent', label: 'Pai/Mãe' },
  { value: 'step_parent', label: 'Padrasto/Madrasta' },
  { value: 'grandparent', label: 'Avô/Avó' },
  { value: 'sibling', label: 'Irmão/Irmã' },
  { value: 'spouse', label: 'Cônjuge' },
  { value: 'former_spouse', label: 'Ex-cônjuge' },
  { value: 'sibling_in_law', label: 'Cunhado(a)' },
  { value: 'partner', label: 'Namorado(a)' },
  { value: 'uncle_aunt', label: 'Tio(a)' },
  { value: 'great_uncle_aunt', label: 'Tio(a)-avô(ó)' },
  { value: 'cousin', label: 'Primo(a)' },
  { value: 'parent_in_law', label: 'Sogro(a)' },
  { value: 'godparent', label: 'Padrinho/Madrinha' },
  { value: 'friend', label: 'Amigo(a)' },
  { value: 'family_friend', label: 'Amigo(a) da família' },
  { value: 'child', label: 'Filho(a)' },
  { value: 'step_child', label: 'Enteado(a)' },
  { value: 'other', label: 'Outro' },
]

const OCCUPATIONS = [
  { value: 'self_employed', label: 'Conta própria (autônomo, empresário)' },
  { value: 'retired', label: 'Aposentado' },
  { value: 'civil_servant', label: 'Concursado' },
  { value: 'employee', label: 'Funcionário (carteira assinada)' },
  { value: 'other', label: 'Outro' },
]

const GENDERS = [
  { value: 'male', label: 'Masculino' },
  { value: 'female', label: 'Feminino' },
  { value: 'other', label: 'Outro' },
]

const PAID_TUITIONS = ['Nenhuma', '1 (matrícula)', '2', '3', '4', '5']

const STUDENT_DOCUMENTS: Record<
  string,
  { label: string; description: string }
> = {
  personal: {
    label: 'Documento com foto',
    description:
      'RG, CNH ou passaporte, com CPF. Não esqueça de anexar frente e verso.',
  },
  proof_of_registration: {
    label: 'Comprovante de matrícula',
    description:
      'Emitido há menos de 2 meses. Boletos e contratos da faculdade não valem.',
  },
  college_records: {
    label: 'Histórico escolar atualizado',
    description:
      'Da graduação. Dê preferência a PDF; prints do site não valem.',
  },
  address: {
    label: 'Comprovante de endereço',
    description: 'Conta de consumo ou fatura dos últimos 2 meses.',
  },
}

const BACKER_DOCUMENTS: Record<string, { label: string; description: string }> =
  {
    personal: {
      label: 'Documento com foto',
      description: 'Frente e verso, com CPF.',
    },
    address: {
      label: 'Comprovante de endereço',
      description: 'Conta de consumo ou fatura dos últimos 2 meses.',
    },
    proof_of_income: {
      label: 'Comprovante de renda',
      description: 'Holerite ou extrato dos últimos 3 meses.',
    },
    income_tax_return: {
      label: 'Declaração de imposto de renda',
      description: 'Com o recibo de entrega.',
    },
  }

function label(options: { value: string; label: string }[], value: string) {
  return options.find((item) => item.value === value)?.label ?? value
}

function initials(name: string) {
  return (name || 'A')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

const card = { border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }

function Highlight({ children }: { children: ReactNode }) {
  return (
    <Box
      component="b"
      sx={{
        color: 'primary.light',
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {children}
    </Box>
  )
}

function InfoBox({
  icon,
  title,
  text,
}: {
  icon: ReactNode
  title: string
  text: string
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '24px minmax(0, 1fr)',
        gap: 1.25,
        p: 1.75,
        borderRadius: 1,
        bgcolor: 'action.hover',
        '& svg': { color: 'primary.light' },
      }}
    >
      {icon}
      <Box>
        <Typography sx={{ fontWeight: 500 }}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {text}
        </Typography>
      </Box>
    </Box>
  )
}

function CenterState({
  icon,
  title,
  text,
}: {
  icon: ReactNode
  title: string
  text: ReactNode
}) {
  return (
    <Box
      sx={{
        textAlign: 'center',
        display: 'grid',
        gap: 1,
        py: 2,
        justifyItems: 'center',
      }}
    >
      {icon}
      <Typography variant="h6">{title}</Typography>
      <Typography color="text.secondary">{text}</Typography>
    </Box>
  )
}

export type PersonRow = {
  key: string
  name: string
  detail: string
}

function PeopleList({
  rows,
  action,
}: {
  rows: PersonRow[]
  action: (row: PersonRow) => ReactNode
}) {
  return (
    <Box sx={{ display: 'grid' }}>
      {rows.map((row) => (
        <Box
          key={row.key}
          sx={{
            display: 'grid',
            gridTemplateColumns: '36px minmax(0, 1fr) auto',
            gap: 1.25,
            alignItems: 'center',
            py: 1.25,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: 'action.hover',
              color: 'primary.main',
              display: 'grid',
              placeItems: 'center',
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            {initials(row.name)}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap>{row.name}</Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {row.detail}
            </Typography>
          </Box>
          {action(row)}
        </Box>
      ))}
    </Box>
  )
}

function BackerActions({
  onEdit,
  onRemove,
}: {
  onEdit: () => void
  onRemove?: () => void
}) {
  return (
    <Box sx={{ display: 'flex' }}>
      <IconButton size="small" aria-label="Editar" onClick={onEdit}>
        <EditOutlined fontSize="small" />
      </IconButton>
      {onRemove && (
        <IconButton
          size="small"
          aria-label="Remover"
          color="error"
          onClick={onRemove}
        >
          <DeleteOutline fontSize="small" />
        </IconButton>
      )}
    </Box>
  )
}

function AddressFields({
  value,
  onPatch,
  municipalities,
  onCep,
  onStateChange,
}: {
  value: Pick<
    BackerForm,
    | 'zip_code'
    | 'street'
    | 'district'
    | 'number'
    | 'complement'
    | 'municipality_id'
    | 'address_state'
  >
  onPatch: (partial: Partial<BackerForm>) => void
  municipalities: MunicipalityOption[]
  onCep: (cep: CepResponse) => void
  onStateChange: (stateCode: string) => void
}) {
  return (
    <>
      <CepSearchField
        label="CEP"
        value={value.zip_code}
        onChange={(event) => onPatch({ zip_code: event.target.value })}
        onFindCep={onCep}
      />
      <Locked label="Rua" value={value.street} />
      <Locked label="Bairro" value={value.district} />
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
        <TextField
          label="Número"
          value={value.number}
          onChange={(event) => onPatch({ number: event.target.value })}
        />
        <TextField
          label="Complemento"
          value={value.complement}
          onChange={(event) => onPatch({ complement: event.target.value })}
        />
      </Box>
      <TextField
        select
        label="Estado"
        value={value.address_state}
        onChange={(event) => onStateChange(event.target.value)}
      >
        {states.map((item) => (
          <MenuItem key={item.code} value={item.code}>
            {item.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label="Cidade"
        value={value.municipality_id}
        onChange={(event) => onPatch({ municipality_id: event.target.value })}
      >
        {municipalities.map((item) => (
          <MenuItem key={item.id} value={String(item.id)}>
            {item.name}
          </MenuItem>
        ))}
      </TextField>
    </>
  )
}

function CivilFields({
  value,
  onPatch,
  spouseIncome,
}: {
  value: Pick<
    BackerForm,
    'marital' | 'spouse_name' | 'spouse_tax_id' | 'spouse_income'
  >
  onPatch: (partial: Partial<BackerForm>) => void
  spouseIncome?: boolean
}) {
  return (
    <>
      <TextField
        select
        label="Estado civil"
        value={value.marital}
        onChange={(event) => onPatch({ marital: event.target.value })}
      >
        {MARITAL.map((item) => (
          <MenuItem key={item.value} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </TextField>
      {isMarried(value.marital) && (
        <>
          <TextField
            label="Nome do cônjuge"
            value={value.spouse_name}
            onChange={(event) => onPatch({ spouse_name: event.target.value })}
          />
          <Field label="CPF do cônjuge">
            <MaskedInput
              mask="000.000.000-00"
              value={value.spouse_tax_id}
              onChange={(event) =>
                onPatch({ spouse_tax_id: event.target.value })
              }
            />
          </Field>
          {spouseIncome && (
            <MoneyField
              label="Renda bruta mensal pessoal do cônjuge"
              value={value.spouse_income}
              onChange={(spouse_income) => onPatch({ spouse_income })}
            />
          )}
        </>
      )}
    </>
  )
}

function Declarations({
  value,
  onPatch,
}: {
  value: { politically_exposed: string; us_person: string }
  onPatch: (partial: {
    politically_exposed?: string
    us_person?: string
  }) => void
}) {
  return (
    <>
      <PillGroup
        label="É politicamente exposto?"
        info={PEP_INFO}
        value={value.politically_exposed}
        onChange={(politically_exposed) =>
          onPatch({ politically_exposed: String(politically_exposed) })
        }
        options={YES_NO}
      />
      <PillGroup
        label="É US Person?"
        info={US_PERSON_INFO}
        value={value.us_person}
        onChange={(us_person) => onPatch({ us_person: String(us_person) })}
        options={YES_NO}
      />
    </>
  )
}

function BackerScreen({
  role,
  index,
  backer,
  patchBacker,
  minGuarantorIncome,
  municipalities,
  onCep,
  onStateChange,
}: {
  role: BackerRole
  index: number
  backer: BackerForm
  patchBacker: (partial: Partial<BackerForm>) => void
  minGuarantorIncome: number
  municipalities: MunicipalityOption[]
  onCep: (cep: CepResponse) => void
  onStateChange: (stateCode: string) => void
}) {
  if (index === 1) {
    const ageWrong =
      Boolean(backer.birthdate) && !backerAgeOk(role, backer.birthdate)
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <TextField
          label="Nome completo"
          value={backer.name}
          onChange={(event) => patchBacker({ name: event.target.value })}
        />
        <TextField
          select
          label="Parentesco com você"
          value={backer.relationship}
          onChange={(event) =>
            patchBacker({ relationship: event.target.value })
          }
        >
          {RELATIONSHIPS.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>
        <Field label="CPF">
          <MaskedInput
            mask="000.000.000-00"
            value={backer.tax_id}
            disabled={Boolean(backer.key)}
            onChange={(event) => patchBacker({ tax_id: event.target.value })}
          />
        </Field>
        <Field
          label="Data de nascimento"
          help={
            ageWrong
              ? role === 'g'
                ? 'O avalista precisa ter de 18 a 74 anos.'
                : 'O responsável precisa ter 18 anos ou mais.'
              : role === 'g'
                ? 'De 18 a 74 anos.'
                : 'A partir de 18 anos.'
          }
        >
          <TextField
            type="date"
            value={backer.birthdate}
            error={ageWrong}
            onChange={(event) => patchBacker({ birthdate: event.target.value })}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Field>
        <PillGroup
          label="Sexo"
          value={backer.gender}
          onChange={(gender) => patchBacker({ gender: String(gender) })}
          options={GENDERS}
        />
      </Box>
    )
  }

  if (index === 2) {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <TextField
          label="E-mail"
          type="email"
          value={backer.email}
          onChange={(event) => patchBacker({ email: event.target.value })}
        />
        <Field label="Celular">
          <MaskedInput
            mask="(00) 00000-0000"
            value={backer.phone}
            onChange={(event) => patchBacker({ phone: event.target.value })}
          />
        </Field>
        <CivilFields
          value={backer}
          onPatch={patchBacker}
          spouseIncome={role === 'r'}
        />
      </Box>
    )
  }

  if (index === 3) {
    const low =
      role === 'g' &&
      backer.income !== '' &&
      Number(backer.income) < minGuarantorIncome
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Field label="Principal atividade">
          <OptionCards
            value={backer.occupation_type}
            onChange={(value) =>
              patchBacker({ occupation_type: String(value) })
            }
            options={OCCUPATIONS}
          />
        </Field>
        <TextField
          multiline
          minRows={2}
          label="Profissão ou ramo de atuação"
          placeholder="Ex.: administradora, tem uma loja de roupas infantis"
          value={backer.occupation_description}
          onChange={(event) =>
            patchBacker({ occupation_description: event.target.value })
          }
        />
        <MoneyField
          label={
            role === 'r'
              ? 'Renda bruta mensal pessoal'
              : 'Renda bruta mensal familiar'
          }
          value={backer.income}
          onChange={(income) => patchBacker({ income })}
          help={
            role === 'g'
              ? low
                ? `Abaixo da renda mínima de ${reais(minGuarantorIncome)}.`
                : `Pelo menos ${reais(minGuarantorIncome)} (3 vezes a maior parcela).`
              : undefined
          }
        />
        <TextField
          select
          label="Escolaridade"
          value={backer.education_level}
          onChange={(event) =>
            patchBacker({ education_level: event.target.value })
          }
        >
          {SCHOOL.map((item) => (
            <MenuItem key={item} value={item}>
              {item}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <AddressFields
        value={backer}
        onPatch={patchBacker}
        municipalities={municipalities}
        onCep={onCep}
        onStateChange={onStateChange}
      />
      <Declarations value={backer} onPatch={patchBacker} />
    </Box>
  )
}

function sentTypes(person: DocumentPerson) {
  return person.types.filter((type) =>
    person.documents.some((item) => item.type === type),
  ).length
}

function PersonDocuments({
  person,
  uploading,
  onUpload,
  onRemoveDocument,
}: {
  person: DocumentPerson
  uploading?: { personKey: string; type: string }
  onUpload: (personKey: string, type: string, file: File) => void
  onRemoveDocument: (documentKey: string) => void | Promise<unknown>
}) {
  return (
    <Box sx={{ display: 'grid', gap: { xs: 1.5, md: 2 } }}>
      {person.types.map((type) => {
        const meta = person.student
          ? STUDENT_DOCUMENTS[type]
          : BACKER_DOCUMENTS[type]
        const sent = person.documents.filter((item) => item.type === type)
        return (
          <DocumentDropzone
            key={type}
            label={meta.label}
            description={meta.description}
            uploading={
              uploading?.personKey === person.key && uploading.type === type
            }
            approved={
              sent.length > 0 &&
              sent.every((item) => item.status === 'approved')
            }
            files={sent.map((item) => ({
              key: item.key,
              name: item.original_name,
              isPdf: item.original_name.toLowerCase().endsWith('.pdf'),
            }))}
            onDrop={(picked) =>
              picked.forEach((file) => onUpload(person.key, type, file))
            }
            onRemove={onRemoveDocument}
          />
        )
      })}
    </Box>
  )
}

/** Um bloco por pessoa: abre no primeiro com pendência e, ao completar um, passa ao próximo. */
function DocumentGroups({
  people,
  uploading,
  onUpload,
  onRemoveDocument,
}: {
  people: DocumentPerson[]
  uploading?: { personKey: string; type: string }
  onUpload: (personKey: string, type: string, file: File) => void
  onRemoveDocument: (documentKey: string) => void | Promise<unknown>
}) {
  const pending = (list: DocumentPerson[]) =>
    list.find((person) => sentTypes(person) < person.types.length)?.key ?? null
  const [open, setOpen] = useState<string | null>(() => pending(people))
  const completeKeys = people
    .filter((person) => sentTypes(person) === person.types.length)
    .map((person) => person.key)
  const completeSignature = completeKeys.join(',')
  const [seenComplete, setSeenComplete] = useState(completeSignature)
  if (seenComplete !== completeSignature) {
    setSeenComplete(completeSignature)
    const justCompleted =
      open !== null &&
      completeKeys.includes(open) &&
      !seenComplete.split(',').includes(open)
    if (justCompleted) setOpen(pending(people))
  }

  if (people.length === 1) {
    return (
      <PersonDocuments
        person={people[0]}
        uploading={uploading}
        onUpload={onUpload}
        onRemoveDocument={onRemoveDocument}
      />
    )
  }

  return (
    <Box sx={{ display: 'grid', gap: { xs: 1, md: 1.5 } }}>
      {people.map((person) => {
        const sent = sentTypes(person)
        const complete = sent === person.types.length
        const expanded = open === person.key
        return (
          <Accordion
            key={person.key}
            expanded={expanded}
            onChange={(_, next) => setOpen(next ? person.key : null)}
            disableGutters
            elevation={0}
            sx={{
              border: 1,
              borderColor: expanded ? 'primary.light' : 'divider',
              borderRadius: '8px !important',
              '&::before': { display: 'none' },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                px: { xs: 1.25, md: 2 },
                minHeight: 0,
                '& .MuiAccordionSummary-content': {
                  my: { xs: 1, md: 1.5 },
                  minWidth: 0,
                },
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '32px minmax(0, 1fr)',
                    md: '40px minmax(0, 1fr)',
                  },
                  gap: { xs: 1.25, md: 1.5 },
                  alignItems: 'center',
                  width: '100%',
                  pr: { xs: 0.5, md: 1 },
                }}
              >
                <Box
                  sx={{
                    width: { xs: 32, md: 40 },
                    height: { xs: 32, md: 40 },
                    borderRadius: '50%',
                    bgcolor: complete ? 'success.main' : 'primary.main',
                    color: 'common.white',
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: { xs: 12, md: 14 },
                    fontWeight: 500,
                  }}
                >
                  {complete ? (
                    <CheckCircle fontSize="small" />
                  ) : (
                    initials(person.name)
                  )}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 500 }} noWrap>
                    {person.name}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      columnGap: 1,
                      rowGap: 0.5,
                      mt: 0.25,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {person.role}
                    </Typography>
                    <Chip
                      size="small"
                      color={complete ? 'success' : 'default'}
                      variant={complete ? 'filled' : 'outlined'}
                      label={
                        complete
                          ? 'Completo'
                          : `${sent} de ${person.types.length} enviados`
                      }
                    />
                  </Box>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails
              sx={{ px: { xs: 1.25, md: 2 }, pt: 0, pb: { xs: 1.5, md: 2 } }}
            >
              <PersonDocuments
                person={person}
                uploading={uploading}
                onUpload={onUpload}
                onRemoveDocument={onRemoveDocument}
              />
            </AccordionDetails>
          </Accordion>
        )
      })}
    </Box>
  )
}

export type DocumentPerson = {
  key: string
  name: string
  role: string
  types: readonly string[]
  student: boolean
  documents: FinancialPerson['documents']
}

export function InternshipScreens({
  step,
  form,
  patch,
  patchBacker,
  colleges,
  onCollegeSearch,
  semester,
  minSemester,
  preview,
  previewError,
  identity,
  municipalities,
  onCep,
  onStateChange,
  responsibles,
  guarantors,
  onEditBacker,
  onRemoveBacker,
  onAddBacker,
  onResponsibleGuarantor,
  minGuarantorIncome,
  scrPeople,
  documentPeople,
  onUpload,
  onRemoveDocument,
  uploading,
  phoneVerified,
  denied,
  collegeName,
  proposal,
  proposalExpired,
  reissuing,
  onReissue,
}: {
  step: InternshipStep
  form: InternshipForm
  patch: (partial: Partial<InternshipForm>) => void
  patchBacker: (partial: Partial<BackerForm>) => void
  colleges: CollegeOption[]
  onCollegeSearch: (value: string) => void
  semester: number
  minSemester: number
  preview: InternshipPreview | undefined
  previewError: boolean
  identity: { name: string; taxId: string; email: string; phone: string }
  municipalities: MunicipalityOption[]
  onCep: (cep: CepResponse) => void
  onStateChange: (stateCode: string) => void
  responsibles: PersonRow[]
  guarantors: PersonRow[]
  onEditBacker: (role: BackerRole, key: string) => void
  onRemoveBacker: (role: BackerRole, key: string) => void
  onAddBacker: (role: BackerRole) => void
  onResponsibleGuarantor: (key: string, guarantor: boolean) => void
  minGuarantorIncome: number
  scrPeople: { key: string; name: string; role: string; email: string }[]
  documentPeople: DocumentPerson[]
  onUpload: (personKey: string, type: string, file: File) => void
  onRemoveDocument: (documentKey: string) => void | Promise<unknown>
  uploading?: { personKey: string; type: string }
  phoneVerified: boolean
  denied: boolean
  collegeName: string
  proposal: FinancialProposal | null
  proposalExpired: boolean
  reissuing: boolean
  onReissue: () => void
}) {
  const role = editorRole(step)
  if (role) {
    return (
      <BackerScreen
        role={role}
        index={Number(step.slice(1))}
        backer={form.backer}
        patchBacker={patchBacker}
        minGuarantorIncome={minGuarantorIncome}
        municipalities={municipalities}
        onCep={onCep}
        onStateChange={onStateChange}
      />
    )
  }

  if (step === 'fac') {
    const options = colleges.filter((item) => item.college_type === 'private')
    const college = options.find((item) => item.key === form.collegeKey) ?? null
    const tuition = Number(form.tuition)
    const tuitionWrong =
      form.tuition !== '' && (tuition < 3000 || tuition > 18000)
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Autocomplete
          options={options}
          value={college}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.key === value.key}
          onInputChange={(_, value) => onCollegeSearch(value)}
          onChange={(_, option) => patch({ collegeKey: option?.key ?? '' })}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Instituição de ensino"
              helperText="Só faculdades privadas. Não é preciso convênio com a Alume."
            />
          )}
        />
        <MoneyField
          label="Valor da mensalidade"
          value={form.tuition}
          onChange={(value) => patch({ tuition: value })}
          help={
            tuitionWrong
              ? tuition < 3000
                ? 'A mensalidade não pode ser menor que R$ 3.000,00.'
                : 'A mensalidade não pode ser maior que R$ 18.000,00.'
              : 'Entre R$ 3.000 e R$ 18.000.'
          }
        />
        <Field
          label="Previsão de formatura"
          help={
            semester >= minSemester
              ? `Você está no ${semester}º semestre.`
              : `O Financiamento Estudantil começa no ${minSemester}º semestre. Pela data, você está no ${Math.max(semester, 1)}º.`
          }
        >
          <AllowanceGraduation
            value={form.graduation}
            onChange={(graduation) => patch({ graduation })}
          />
        </Field>
      </Box>
    )
  }

  if (step === 'sem') {
    const current = preview?.limits.current_semester ?? semester
    const options = preview?.limits.start_semesters ?? []
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <OptionCards
          value={form.startSemester ? String(form.startSemester) : ''}
          onChange={(value) =>
            patch({ startSemester: Number(value), paidTuitions: 0 })
          }
          options={options.map((item) => ({
            value: String(item),
            label: `${item}º semestre${item === current ? ' · o atual' : ''}`,
          }))}
        />
        {form.startSemester === current && (
          <PillGroup
            label="Quantas mensalidades deste semestre já foram pagas (incluindo a matrícula)?"
            help="As que ainda não foram pagas entram no financiamento."
            value={String(form.paidTuitions)}
            onChange={(value) => patch({ paidTuitions: Number(value) })}
            options={PAID_TUITIONS.map((text, index) => ({
              value: String(index),
              label: text,
            }))}
          />
        )}
      </Box>
    )
  }

  if (step === 'divida') {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <ToggleRow
          label="Possuo dívidas ou renegociações com a faculdade"
          checked={form.hasDebt}
          onChange={(hasDebt) => patch({ hasDebt })}
        />
        {form.hasDebt ? (
          <MoneyField
            label="Valor da dívida"
            value={form.debt}
            onChange={(debt) => patch({ debt })}
            help="Até R$ 200.000,00. O valor entra no financiamento."
          />
        ) : (
          <Typography color="text.secondary">
            Se não tiver, é só continuar.
          </Typography>
        )}
      </Box>
    )
  }

  if (step === 'res') {
    if (previewError) {
      return (
        <Alert severity="error">
          Não conseguimos calcular a simulação. Volte e confira os dados da
          faculdade.
        </Alert>
      )
    }
    if (!preview) {
      return (
        <Box sx={{ ...card, display: 'grid', gap: 2 }}>
          <Skeleton width="40%" />
          {Array.from({ length: 3 }, (_, index) => (
            <Box key={index}>
              <Skeleton width="30%" />
              <Skeleton width="80%" />
            </Box>
          ))}
        </Box>
      )
    }
    if (!preview.viable) {
      return (
        <CenterState
          icon={<InfoOutlined color="error" sx={{ fontSize: 48 }} />}
          title="Fora da nossa política de crédito"
          text="Com base nos dados informados, a simulação do Financiamento Estudantil não é possível. Você ainda pode contar com outras soluções da Alume."
        />
      )
    }
    const [during, after] = preview.summary.phases
    const years = Math.floor(after.installments / 12)
    const months = after.installments % 12
    const period = [
      years ? `${years} ${years === 1 ? 'ano' : 'anos'}` : '',
      months ? `${months} ${months === 1 ? 'mês' : 'meses'}` : '',
    ]
      .filter(Boolean)
      .join(' e ')
    const tuition = Number(form.tuition)
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Box sx={{ ...card, display: 'grid', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
            }}
          >
            <Typography sx={{ fontWeight: 500 }}>Alume Internato</Typography>
            <Typography variant="body2" color="text.secondary">
              Juros de {pct(preview.summary.monthly_rate)} a.m.
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 500, mb: 0.5 }}>
              Durante o curso
            </Typography>
            <Typography>
              Ao invés de pagar {during.installments}x de {reais(tuition)}, você
              paga apenas{' '}
              <Highlight>
                {during.installments}x de {reais(during.value)}
              </Highlight>
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 500, mb: 0.5 }}>
              Após a formatura
            </Typography>
            <Typography>
              Parcelas de <Highlight>{reais(after.value)}</Highlight> / mês por{' '}
              {period}.
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 500, mb: 0.5 }}>
              Início do financiamento
            </Typography>
            <Typography>{preview.plan.start_semester}º semestre</Typography>
          </Box>
          {preview.plan.unpaid_tuitions + preview.plan.college_debt > 0 && (
            <Typography variant="body2" color="text.secondary">
              Inclui{' '}
              {reais(preview.plan.unpaid_tuitions + preview.plan.college_debt)}{' '}
              de mensalidades em aberto e dívidas.
            </Typography>
          )}
        </Box>
        <Box
          component="ul"
          sx={{
            m: 0,
            pl: 2.25,
            display: 'grid',
            gap: 0.5,
            fontSize: 13.5,
            color: 'text.secondary',
          }}
        >
          <li>
            Os valores apresentados são estimativas e estão sujeitos à análise
            de crédito.
          </li>
          <li>
            Caso o pagamento seja realizado antecipadamente e/ou em menos
            parcelas o valor total a ser pago será menor.
          </li>
        </Box>
      </Box>
    )
  }

  if (step === 'eu') {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        {!phoneVerified && (
          <Alert
            icon={false}
            severity="warning"
            action={
              <Button
                size="small"
                variant="text"
                color="warning"
                component={RouterLink}
                to={routes.account}
              >
                Verificar telefone
              </Button>
            }
          >
            Cadastre e verifique seu celular no perfil para continuar.
          </Alert>
        )}
        <Locked label="Nome completo" value={identity.name} />
        <Locked label="CPF" value={identity.taxId} />
        <Locked label="E-mail" value={identity.email} />
        <Locked label="Celular" value={identity.phone} />
        <Field label="Data de nascimento">
          <TextField
            type="date"
            value={form.birthdate}
            onChange={(event) => patch({ birthdate: event.target.value })}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Field>
        <PillGroup
          label="Sexo"
          value={form.gender}
          onChange={(value) => patch({ gender: String(value) })}
          options={GENDERS}
        />
      </Box>
    )
  }

  if (step === 'end') {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <AddressFields
          value={form}
          onPatch={patch}
          municipalities={municipalities}
          onCep={onCep}
          onStateChange={onStateChange}
        />
      </Box>
    )
  }

  if (step === 'civil') {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Field label="Estado civil">
          <OptionCards
            value={form.marital}
            onChange={(value) => patch({ marital: String(value) })}
            options={MARITAL}
          />
        </Field>
        {isMarried(form.marital) && (
          <>
            <TextField
              label="Nome completo do cônjuge"
              value={form.spouse_name}
              onChange={(event) => patch({ spouse_name: event.target.value })}
            />
            <Field label="CPF do cônjuge">
              <MaskedInput
                mask="000.000.000-00"
                value={form.spouse_tax_id}
                onChange={(event) =>
                  patch({ spouse_tax_id: event.target.value })
                }
              />
            </Field>
          </>
        )}
        <Declarations value={form} onPatch={patch} />
      </Box>
    )
  }

  if (step === 'traj') {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <TextField
          select
          label="Onde estudou no ensino médio?"
          value={form.high_school_type}
          onChange={(event) => patch({ high_school_type: event.target.value })}
        >
          {HIGH_SCHOOL.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>
        <PillGroup
          label="Já recebeu prêmio ou bolsa por mérito acadêmico?"
          value={form.academic_award}
          onChange={(value) => patch({ academic_award: String(value) })}
          options={YES_NO}
        />
        {form.academic_award === 'yes' && (
          <TextField
            label="Descreva o prêmio ou bolsa"
            value={form.academic_award_description}
            onChange={(event) =>
              patch({ academic_award_description: event.target.value })
            }
          />
        )}
        <PillGroup
          label="Já precisou trancar o curso?"
          value={form.course_locked}
          onChange={(value) => patch({ course_locked: String(value) })}
          options={YES_NO}
        />
        {form.course_locked === 'yes' && (
          <TextField
            multiline
            minRows={3}
            label="Descreva o motivo"
            value={form.course_locked_reason}
            onChange={(event) =>
              patch({ course_locked_reason: event.target.value })
            }
          />
        )}
      </Box>
    )
  }

  if (step === 'planos') {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <PillGroup
          multiple
          label="O que pretende fazer?"
          value={form.plans}
          onChange={(value) => patch({ plans: value as string[] })}
          options={PLANS}
        />
        {form.plans.includes('other') && (
          <TextField
            label="Qual?"
            value={form.plans_other}
            onChange={(event) => patch({ plans_other: event.target.value })}
          />
        )}
        <Field label="Quando pretende fazer residência médica?">
          <OptionCards
            value={form.residency_timing}
            onChange={(value) => patch({ residency_timing: String(value) })}
            options={RESIDENCY}
          />
        </Field>
      </Box>
    )
  }

  if (step === 'fin') {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <PillGroup
          multiple
          label="Como a faculdade foi paga até agora? Escolha quantas quiser."
          value={form.payment_sources}
          onChange={(value) => patch({ payment_sources: value as string[] })}
          options={PAYMENT_SOURCES}
        />
        <TextField
          multiline
          minRows={3}
          label="Conte um pouco sobre como foi feito o pagamento (opcional)"
          value={form.payment_story}
          onChange={(event) => patch({ payment_story: event.target.value })}
        />
        <TextField
          multiline
          minRows={3}
          label="Por que precisa do financiamento?"
          value={form.financing_reason}
          onChange={(event) => patch({ financing_reason: event.target.value })}
        />
      </Box>
    )
  }

  if (step === 'resp') {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <OptionCards
          value={form.selfResponsible}
          onChange={(value) =>
            patch({ selfResponsible: value as 'yes' | 'no' })
          }
          options={[
            { value: 'yes', label: 'Eu mesmo' },
            { value: 'no', label: 'Outra pessoa' },
          ]}
        />
        {form.selfResponsible === 'yes' && (
          <MoneyField
            label="Sua renda bruta mensal pessoal"
            value={form.self_income}
            onChange={(self_income) => patch({ self_income })}
          />
        )}
      </Box>
    )
  }

  if (step === 'rlist') {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <PeopleList
          rows={responsibles}
          action={(row) => (
            <BackerActions
              onEdit={() => onEditBacker('r', row.key)}
              onRemove={
                responsibles.length > 1
                  ? () => onRemoveBacker('r', row.key)
                  : undefined
              }
            />
          )}
        />
        <Button
          variant="outlined"
          startIcon={<PersonAdd />}
          onClick={() => onAddBacker('r')}
        >
          Incluir outro responsável
        </Button>
        <Typography variant="body2" color="text.secondary">
          Inclua mais de um se outras pessoas também ajudam a pagar.
        </Typography>
      </Box>
    )
  }

  if (step === 'av') {
    const guarantorKeys = new Set(guarantors.map((row) => row.key))
    const responsibleKeys = new Set(responsibles.map((row) => row.key))
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        {responsibles.map((row) => (
          <PillGroup
            key={row.key}
            label={
              responsibles.length > 1
                ? `${row.name} será avalista?`
                : 'O responsável será avalista?'
            }
            help={responsibles.length > 1 ? undefined : row.name}
            value={
              guarantorKeys.has(row.key)
                ? 'yes'
                : form.guarantorDeclined.includes(row.key)
                  ? 'no'
                  : ''
            }
            onChange={(value) =>
              onResponsibleGuarantor(row.key, value === 'yes')
            }
            options={YES_NO}
          />
        ))}
        {guarantors.length > 0 && (
          <Field label="Avalistas">
            <PeopleList
              rows={guarantors}
              action={(row) => (
                <BackerActions
                  onEdit={() => onEditBacker('g', row.key)}
                  onRemove={() =>
                    responsibleKeys.has(row.key)
                      ? onResponsibleGuarantor(row.key, false)
                      : onRemoveBacker('g', row.key)
                  }
                />
              )}
            />
          </Field>
        )}
        {guarantors.length < MAX_GUARANTORS ? (
          <Button
            variant="outlined"
            startIcon={<PersonAdd />}
            onClick={() => onAddBacker('g')}
          >
            Adicionar avalista
          </Button>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Você chegou ao limite de {MAX_GUARANTORS} avalistas.
          </Typography>
        )}
        <InfoBox
          icon={<InfoOutlined />}
          title={`Renda mínima do avalista: ${reais(minGuarantorIncome)}`}
          text="3 vezes a maior parcela da simulação. Nossa equipe confere a comprovação de renda depois."
        />
      </Box>
    )
  }

  if (step === 'scr') {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <ToggleRow
          label={`Eu, ${identity.name}, autorizo a consulta ao SCR do Banco Central`}
          checked={form.scr}
          onChange={(scr) => patch({ scr })}
        />
        <Typography variant="body2" color="text.secondary">
          <Box
            component="a"
            href="https://app.alume.com/scr-terms"
            target="_blank"
            rel="noreferrer"
            sx={{ color: 'primary.main', fontWeight: 500 }}
          >
            Leia o termo de autorização de consulta ao SCR
          </Box>
        </Typography>
        {scrPeople.length > 0 && (
          <Field label="Também precisam autorizar">
            <PeopleList
              rows={scrPeople.map((item) => ({
                key: item.key,
                name: item.name,
                detail: `${item.role} · ${item.email}`,
              }))}
              action={() => <Chip size="small" label="Pendente" />}
            />
          </Field>
        )}
        {scrPeople.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            Nossa equipe pede a autorização de cada um pelo e-mail informado.
          </Typography>
        )}
      </Box>
    )
  }

  if (step === 'docs') {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: 'action.hover',
          }}
        >
          <InfoOutlined color="primary" />
          <Typography variant="body2">
            <b>Atenção:</b> dê preferência a documentos com verificadores
            digitais de autenticidade.
          </Typography>
        </Box>
        <DocumentGroups
          people={documentPeople}
          uploading={uploading}
          onUpload={onUpload}
          onRemoveDocument={onRemoveDocument}
        />
      </Box>
    )
  }

  if (step === 'pre') {
    return (
      <CenterState
        icon={<HourglassTop color="primary" sx={{ fontSize: 48 }} />}
        title="Estamos fazendo a pré-análise"
        text="Assim que for aprovada, avisamos você para enviar os documentos."
      />
    )
  }

  if (step === 'carta') {
    const student = documentPeople.find((person) => person.student)
    const sent =
      student?.documents.filter((item) => item.type === ENABLING_LETTER) ?? []
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        {student && (
          <DocumentDropzone
            label="Carta de habilitação assinada"
            description="Preenchida e assinada, em PDF ou foto."
            uploading={
              uploading?.personKey === student.key &&
              uploading.type === ENABLING_LETTER
            }
            approved={
              sent.length > 0 &&
              sent.every((item) => item.status === 'approved')
            }
            files={sent.map((item) => ({
              key: item.key,
              name: item.original_name,
              isPdf: item.original_name.toLowerCase().endsWith('.pdf'),
            }))}
            onDrop={(picked) =>
              picked.forEach((file) =>
                onUpload(student.key, ENABLING_LETTER, file),
              )
            }
            onRemove={onRemoveDocument}
          />
        )}
      </Box>
    )
  }

  if (step === 'analise') {
    return denied ? (
      <CenterState
        icon={<Cancel color="error" sx={{ fontSize: 48 }} />}
        title="Seu pedido não foi aprovado"
        text="Infelizmente não poderemos te apoiar neste momento."
      />
    ) : (
      <CenterState
        icon={<HourglassTop color="primary" sx={{ fontSize: 48 }} />}
        title="Estamos preparando sua proposta"
        text="Analisamos seus documentos para enviar uma proposta. Avisamos pelo sino, WhatsApp e e-mail."
      />
    )
  }

  if (step === 'proposta') {
    if (!proposal)
      return <Typography>A proposta ainda não está disponível.</Typography>
    const values = proposal.cashflow.map((item) => item.value)
    const during = proposal.disbursements
    const first = proposal.cashflow[0]
    const after = proposal.cashflow[during]
    const validUntil = brazilDate(proposal.expiration_date)
    const row = (name: string, value: ReactNode) => (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          gap: 1.5,
          py: 1.25,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography color="text.secondary">{name}</Typography>
        <Typography
          sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
        >
          {value}
        </Typography>
      </Box>
    )
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Box sx={{ ...card, p: 0, overflow: 'hidden' }}>
          <Box
            sx={{
              bgcolor: 'primary.light',
              color: 'common.white',
              px: 2,
              py: 1.5,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Typography sx={{ fontWeight: 500 }}>Alume Internato</Typography>
            <Typography>
              {pct(Number(proposal.monthly_interest))} a.m.
            </Typography>
          </Box>
          <Box sx={{ px: 2, pb: 1 }}>
            {row('Valor financiado', <b>{reais(Number(proposal.amount))}</b>)}
            {first &&
              row(
                'Durante o curso',
                <>
                  <b>{during} parcelas</b> de {reais(first.value)}
                </>,
              )}
            {after &&
              row(
                'Após a formatura',
                <>
                  <b>{values.length - during} parcelas</b> de{' '}
                  {reais(after.value)}
                </>,
              )}
            {first && row('1ª parcela', brazilDate(first.due_date))}
            {row('Válida até', validUntil)}
          </Box>
        </Box>
        <InfoBox
          icon={<AccountBalance />}
          title="O dinheiro vai direto para a faculdade"
          text={`A Alume paga as mensalidades à ${collegeName}. Você paga a Alume.`}
        />
        {proposalExpired && (
          <Alert
            severity="warning"
            action={
              <Button
                color="inherit"
                size="small"
                disabled={reissuing}
                onClick={onReissue}
              >
                Reemitir proposta expirada
              </Button>
            }
          >
            Esta proposta expirou em {validUntil}.
          </Alert>
        )}
      </Box>
    )
  }

  if (step === 'assinar') {
    return (
      <CenterState
        icon={<Draw color="primary" sx={{ fontSize: 48 }} />}
        title="Assinatura da CCB"
        text="A Cédula de Crédito Bancário é gerada automaticamente. Avisamos quando estiver pronta para você e os avalistas assinarem."
      />
    )
  }

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <CenterState
        icon={<Celebration color="success" sx={{ fontSize: 48 }} />}
        title="Financiamento contratado!"
        text={`A Alume passa a pagar as mensalidades da ${collegeName}.`}
      />
      <InfoBox
        icon={<Autorenew />}
        title="Renovação a cada semestre"
        text="No fim do semestre, você renova pelo Resumo: revisa os dados e envia comprovante de endereço e de renda atualizados."
      />
    </Box>
  )
}

export function backerRows(
  links: { person: FinancialPerson; relationship?: string | null }[],
): PersonRow[] {
  return links.map(({ person, relationship }) => ({
    key: person.key,
    name: person.name,
    detail: [
      relationship ? label(RELATIONSHIPS, relationship) : '',
      person.monthly_income != null
        ? `renda ${reais(Number(person.monthly_income))}`
        : '',
    ]
      .filter(Boolean)
      .join(' · '),
  }))
}
